import {
  useAiEditor,
  SuggestionPopover,
  aiSuggestionPluginKey,
  type AiSuggestionCustomResolverOptions,
  type Suggestion,
} from '@/tiptap'
import { createFileRoute } from '@tanstack/react-router'
import { EditorContent } from '@tiptap/react'
import { useEffect, useRef, useState } from 'react'

export const Route = createFileRoute('/demo/tiptap/ai-suggestion')({
  component: AiSuggestionDemo,
})

const initialContent = `
<h1>
  AI Suggestion Demo
</h1>
<p>
  This is a demonstration of the AI Suggestion extension for Tiptap. The text bellow contains some mistaeks that the AI should be able to identify and correct.
</p>
<p>
  Tiptap is a headless editor framework for the web. It is build on top of ProseMirror and provides a modern and flexible way to build rich text editors. It is highly customizable and extensible, allowing developers to create their own extensions and custom functionality.
</p>
`

function AiSuggestionDemo() {
  const [popoverElement, setPopoverElement] = useState<HTMLElement | null>(null)
  const popoverRef = useRef<HTMLElement | null>(null)

  const { editor } = useAiEditor({
    content: initialContent,
    suggestionOptions: {
      popoverRef,
      // Mock resolver for demo purposes
      async aiSuggestionResolver(opts: AiSuggestionCustomResolverOptions) {
        console.log('[Extension] resolver called', opts)
        const { editor, rules } = opts

        // Simulate API call
        await new Promise((resolve) => setTimeout(resolve, 1000))

        const suggestionsToFind = [
          { text: 'mistaeks', ruleId: '1', replacement: 'mistakes' },
          { text: 'bellow', ruleId: '1', replacement: 'below' },
          { text: 'build', ruleId: '2', replacement: 'built' },
        ]

        const foundSuggestions: Suggestion[] = []
        let suggestionIdCounter = 1

        try {
          // Get the editor's document
          const doc = editor.state.doc

          // Helper function to safely check text content
          const findInNode = (node: any, pos: number) => {
            if (!node.isText || !node.text) return

            suggestionsToFind.forEach((sugToFind) => {
              let index = node.text.indexOf(sugToFind.text)

              while (index !== -1) {
                const from = pos + index
                const to = from + sugToFind.text.length
                const rule = rules.find((r) => r.id === sugToFind.ruleId)

                if (rule) {
                  console.log('[Extension] Found match:', {
                    text: sugToFind.text,
                    from,
                    to,
                    nodeText: node.text,
                    rule: rule.title,
                  })

                  foundSuggestions.push({
                    id: (suggestionIdCounter++).toString(),
                    deleteRange: { from, to },
                    deleteText: sugToFind.text,
                    replacementOptions: [
                      {
                        id: '1',
                        addText: sugToFind.replacement,
                        addSlice: null,
                      },
                    ],
                    rule,
                    isRejected: false,
                    deleteSlice: null,
                  })
                }

                index = node.text.indexOf(sugToFind.text, index + 1)
              }
            })
          }

          // Traverse the document
          doc.descendants((node, pos) => {
            findInNode(node, pos)
          })

          console.log('[Extension] Found suggestions:', foundSuggestions)
          return foundSuggestions
        } catch (error) {
          console.error('[Extension] Error in resolver:', error)
          return []
        }
      },
    },
  })

  const pluginState =
    editor?.state && aiSuggestionPluginKey.getState(editor.state)
  const suggestions = pluginState?.suggestions || []
  const selectedSuggestion = pluginState?.selectedSuggestionId
    ? pluginState.suggestions.find(
        (s: Suggestion) => s.id === pluginState.selectedSuggestionId
      ) || null
    : null

  useEffect(() => {
    if (selectedSuggestion && popoverRef.current) {
      setPopoverElement(popoverRef.current)
    } else {
      setPopoverElement(null)
    }
  }, [selectedSuggestion, editor, popoverRef])

  return (
    <>
      <div className='mx-auto w-full max-w-4xl space-y-6 p-6'>
        <div className='overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900'>
          <div className='border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 dark:border-gray-600 dark:from-gray-800 dark:to-gray-700'>
            <h1 className='mb-2 text-2xl font-bold text-gray-900 dark:text-white'>
              AI Suggestion Demo
            </h1>
            <p className='text-gray-600 dark:text-gray-300'>
              This demo showcases the AI Suggestion extension. Click on the
              highlighted suggestions to accept or reject them.
            </p>
          </div>

          <div className='space-y-4 border-b border-gray-200 p-6 dark:border-gray-700'>
            {/* Loading indicator */}
            {pluginState?.loading && (
              <div className='mb-2 flex items-center gap-2 text-sm text-blue-600 dark:text-blue-300'>
                <svg className='h-4 w-4 animate-spin' viewBox='0 0 24 24'>
                  <circle
                    className='opacity-25'
                    cx='12'
                    cy='12'
                    r='10'
                    stroke='currentColor'
                    strokeWidth='4'
                    fill='none'
                  />
                  <path
                    className='opacity-75'
                    fill='currentColor'
                    d='M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z'
                  />
                </svg>
                Loading suggestions...
              </div>
            )}
            <div className='flex items-center gap-4'>
              <button
                onClick={() => {
                  editor?.commands.loadAiSuggestions()
                }}
                className='rounded bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700'
              >
                Load Suggestions
              </button>
              <button
                onClick={() => editor?.commands.applyAllAiSuggestions()}
                className='rounded bg-green-600 px-3 py-2 text-sm text-white hover:bg-green-700'
              >
                Accept All
              </button>
              <button
                onClick={() => editor?.commands.rejectAllAiSuggestions()}
                className='rounded bg-red-600 px-3 py-2 text-sm text-white hover:bg-red-700'
              >
                Reject All
              </button>
              <button
                onClick={() => console.log(editor?.getJSON())}
                className='rounded bg-gray-600 px-3 py-2 text-sm text-white hover:bg-gray-700'
              >
                Print JSON
              </button>
            </div>
            {suggestions.length > 0 && (
              <div className='space-y-2'>
                <h4 className='text-sm font-medium text-gray-700 dark:text-gray-300'>
                  Suggestions:
                </h4>
                <div className='space-y-1'>
                  {suggestions.map((suggestion) => (
                    <div
                      key={suggestion.id}
                      className='flex items-center justify-between rounded bg-gray-50 p-2 text-sm dark:bg-gray-800'
                    >
                      <div className='flex items-center gap-2'>
                        <span
                          className='h-2 w-2 rounded-full'
                          style={{ backgroundColor: suggestion.rule.color }}
                        />
                        <span className='font-medium'>
                          {suggestion.rule.title}
                        </span>
                        <span className='text-gray-500'>
                          {suggestion.deleteText} →{' '}
                          {suggestion.replacementOptions[0].addText}
                        </span>
                      </div>
                      <div className='flex gap-1'>
                        <button
                          onClick={() =>
                            editor?.commands.selectAiSuggestion(suggestion.id)
                          }
                          className='rounded bg-blue-100 px-2 py-1 text-xs text-blue-700 hover:bg-blue-200'
                        >
                          Select
                        </button>
                        <button
                          onClick={() =>
                            editor?.commands.applyAiSuggestion({
                              suggestionId: suggestion.id,
                            })
                          }
                          className='rounded bg-green-100 px-2 py-1 text-xs text-green-700 hover:bg-green-200'
                        >
                          Accept
                        </button>
                        <button
                          onClick={() =>
                            editor?.commands.rejectAiSuggestion(suggestion.id)
                          }
                          className='rounded bg-red-100 px-2 py-1 text-xs text-red-700 hover:bg-red-200'
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className='p-6'>
            <EditorContent editor={editor} />
          </div>
        </div>
      </div>

      {editor && (
        <SuggestionPopover
          editor={editor}
          element={popoverElement}
          selectedSuggestion={selectedSuggestion}
        />
      )}
    </>
  )
}

export default AiSuggestionDemo
